import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useWebSocket } from '../../hooks/useWebSocket';

export default function ProjectEditor() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generationPhase, setGenerationPhase] = useState('');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationMessage, setGenerationMessage] = useState('');
  const [showCriteria, setShowCriteria] = useState(true);
  const [showConversation, setShowConversation] = useState(false);
  const [regeneratingStickerId, setRegeneratingStickerId] = useState(null);
  const [selectedQuality, setSelectedQuality] = useState('standard');
  const [selectedShape, setSelectedShape] = useState('square'); // 'square' | 'shape'

  // Ref-based lock: prevents rapid double-clicks from firing before React state updates
  const generatingRef = useRef(false);

  // WebSocket handler for real-time progress updates
  const handleWebSocketMessage = (message) => {
    if (message.type === 'generation-progress') {
      setGenerationPhase(message.phase || 'images');
      setGenerationProgress(Math.round((message.completed / message.total) * 100));
      setGenerationMessage(message.current || '');

      // If we received image data, add it to the project immediately
      if (message.imageData && message.position !== undefined) {
        setProject(prev => {
          if (!prev) return prev;

          const newSticker = {
            id: Date.now() + message.position, // Temporary ID
            project_id: prev.id,
            prompt: message.current,
            image_url: `data:image/png;base64,${message.imageData}`,
            position: message.position,
            shape: selectedShape === 'shape' ? 1 : 0
          };

          const existingStickers = prev.stickers || [];
          const updatedStickers = [...existingStickers];

          // Replace or add sticker at position
          const existingIndex = updatedStickers.findIndex(s => s.position === message.position);
          if (existingIndex >= 0) {
            updatedStickers[existingIndex] = newSticker;
          } else {
            updatedStickers.push(newSticker);
          }

          return {
            ...prev,
            stickers: updatedStickers.sort((a, b) => a.position - b.position)
          };
        });
      }
    } else if (message.type === 'generation-complete') {
      generatingRef.current = false;
      setGenerating(false);
      setGenerationProgress(100);
      setGenerationMessage('Complete! Refreshing...');

      // Refresh project data to get final stickers from database
      setTimeout(() => {
        fetchProject();
      }, 1000);
    } else if (message.type === 'generation-error') {
      generatingRef.current = false;
      setGenerating(false);
      setGenerationProgress(0);
      setGenerationMessage('');
      alert(`Generation failed: ${message.error || 'Unknown error'}. Please try again.`);
    }
  };

  // Connect to WebSocket
  useWebSocket(parseInt(id), handleWebSocketMessage);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await fetch(`/api/projects/${id}`);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Project not found');
        }
        throw new Error('Failed to fetch project');
      }
      const data = await response.json();
      setProject(data);

      // If project is still generating, show progress UI
      if (data.status === 'generating') {
        setGenerating(true);
      }
    } catch (err) {
      console.error('Error fetching project:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateStickers = async () => {
    // Ref guard prevents rapid double-clicks before React state settles
    if (generatingRef.current || generating) {
      console.log('Generation already in progress, ignoring duplicate request');
      return;
    }

    if (!confirm('Generate 12 stickers for this project? This will take 1-2 minutes.')) {
      return;
    }

    // Lock immediately (synchronous — no async gap before this)
    generatingRef.current = true;
    setGenerating(true);
    setGenerationProgress(0);
    setGenerationMessage('Starting generation...');

    try {
      const qualityLabel = selectedQuality === 'hd' ? 'HD (5-6 minutes)' : 'Standard (1-2 minutes)';
      console.log(`Starting generation with ${qualityLabel} quality`);

      // Fire-and-forget: don't await — WebSocket handles all progress updates.
      // If the backend rejects with 409 (already generating), log it.
      fetch(`/api/projects/${id}/generate-stickers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quality: selectedQuality, removeBackground: selectedShape === 'shape' })
      }).then(async (res) => {
        if (res.status === 409) {
          console.log('[Generation] Server rejected duplicate — generation already running');
          generatingRef.current = false;
          setGenerating(false);
        }
      }).catch(() => {
        // Connection may drop on long requests — WebSocket still delivers progress
        console.log('POST connection closed (WebSocket handles progress)');
      });

      console.log('Generation started, listening for WebSocket updates...');
    } catch (err) {
      console.error('Generation error:', err);
      alert('Failed to start generation: ' + err.message);
      generatingRef.current = false;
      setGenerating(false);
    }
  };

  const handleRegenerateSticker = async (stickerId, prompt, position, removeBackground = selectedShape === 'shape') => {
    // Use global quality setting
    const qualityLabel = selectedQuality === 'hd' ? 'HD (1024x1024, slower)' : 'Standard (512x512, faster)';
    const coldStartWarning = '⚠️ Note: First regeneration may take 30-60s (cold start).';

    if (!confirm(`Regenerate sticker #${position + 1} with ${qualityLabel} quality?\n\n${coldStartWarning}`)) return;

    setRegeneratingStickerId(stickerId);

    try {
      console.log(`Regenerating sticker ${stickerId} with ${selectedQuality} quality...`);

      // No timeout needed - server streams heartbeats every 20s to keep connection alive
      const response = await fetch('/api/stickers/regenerate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stickerId,
          prompt,
          quality: selectedQuality,
          removeBackground
        })
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      // Update the sticker in the UI immediately
      setProject(prev => {
        if (!prev) return prev;

        const updatedStickers = prev.stickers.map(s =>
          s.id === stickerId
            ? { ...s, image_url: `data:image/png;base64,${data.imageData}`, quality: data.quality, shape: removeBackground ? 1 : 0 }
            : s
        );

        return { ...prev, stickers: updatedStickers };
      });

      console.log(`Sticker #${position + 1} regenerated successfully (${selectedQuality})`);
    } catch (err) {
      console.error('Regenerate error:', err);
      alert('Failed to regenerate sticker: ' + err.message);
    } finally {
      setRegeneratingStickerId(null);
    }
  };

  const handleRetryFailedSticker = () => {
    alert('To retry failed stickers, click "🔄 Regenerate All" which will generate all 12 stickers again (keeping successful ones or generating new variations).\n\nComing soon: Individual retry with custom prompts!');
  };

  const handleRegenerateAll = () => {
    if (!confirm('Regenerate all stickers? This will replace existing stickers.')) return;
    alert('Regenerate All - Coming soon!');
  };

  const handleExportPDF = async () => {
    try {
      console.log(`[PDF Export] Starting PDF download for project ${id}`);

      // Fetch PDF from backend
      const response = await fetch(`/api/projects/${id}/export-pdf`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${project.theme.replace(/[^a-z0-9]/gi, '_')}-stickers.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download the PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`[PDF Export] PDF "${filename}" downloaded successfully`);
    } catch (err) {
      console.error('[PDF Export] Error:', err);
      alert(`Failed to export PDF: ${err.message}`);
    }
  };

  const handleDeleteProject = async () => {
    if (!confirm('Delete this project? This cannot be undone.')) return;

    try {
      const response = await fetch(`/api/projects/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete project');

      alert('Project deleted successfully');
      navigate('/projects');
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="card">
          <p className="text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="card bg-red-50 border border-red-200">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <Link to="/projects" className="btn-secondary inline-block">
            ← Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  // Parse criteria if it's a string
  const criteria = typeof project.criteria === 'string'
    ? JSON.parse(project.criteria)
    : (project.criteria || {});

  // Parse conversation if it's a string
  const conversation = typeof project.conversation === 'string'
    ? JSON.parse(project.conversation)
    : (project.conversation || []);

  const criteriaFields = [
    { key: 'style', label: 'Visual Style', placeholder: 'e.g., cute, minimalist, bold' },
    { key: 'artStyle', label: 'Art Style', placeholder: 'e.g., cartoon, realistic, vintage' },
    { key: 'subjects', label: 'Subjects/Topics', placeholder: 'e.g., cats, dinosaurs, flowers' },
    { key: 'colors', label: 'Colors', placeholder: 'e.g., blue, pink, pastel' },
    { key: 'borders', label: 'Borders/Layout', placeholder: 'e.g., white border, no border' },
    { key: 'mood', label: 'Mood/Vibe', placeholder: 'e.g., playful, fun, adventurous' },
    { key: 'audience', label: 'Target Audience', placeholder: 'e.g., kids, adults, teens' },
    { key: 'useCase', label: 'Use Case', placeholder: 'e.g., laptop, water bottle, notebook' },
    { key: 'size', label: 'Size Preference', placeholder: 'e.g., small, 2 inches' },
    { key: 'quantity', label: 'Quantity', placeholder: 'e.g., 12 stickers, dozen' }
  ];

  const filledCriteriaCount = Object.values(criteria).filter(v => v && v.trim()).length;
  const hasStickers = project.stickers && project.stickers.length > 0;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Link to="/projects" className="text-indigo-600 hover:text-indigo-800">
            ← Back to Projects
          </Link>
        </div>

        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">{project.theme}</h1>
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                project.status === 'completed' ? 'bg-green-100 text-green-800' :
                project.status === 'generating' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {project.status}
              </span>
              <span>Created: {new Date(project.created_at).toLocaleDateString()}</span>
              {project.sticker_count > 0 && (
                <span>{project.sticker_count} sticker{project.sticker_count !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            {!hasStickers && (
              <button
                onClick={handleGenerateStickers}
                disabled={generating}
                className="btn-primary"
              >
                ✨ Generate {selectedQuality === 'hd' ? 'HD ' : ''}{selectedShape === 'shape' ? 'Shape ' : ''}Stickers
              </button>
            )}
            {hasStickers && (
              <>
                <button onClick={handleRegenerateAll} className="btn-secondary">
                  🔄 Regenerate All
                </button>
                <button onClick={handleExportPDF} className="btn-primary">
                  📄 Export PDF
                </button>
              </>
            )}
            <button
              onClick={handleDeleteProject}
              className="btn-secondary text-red-600 hover:bg-red-50"
            >
              🗑️ Delete
            </button>
          </div>
        </div>

        {/* Global Quality + Shape Selector */}
        <div className="card bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Image Quality</h3>
              <p className="text-sm text-gray-600">
                Applies to all generation and regeneration operations
              </p>
            </div>
            <div className="flex gap-2 bg-white p-1.5 rounded-lg shadow-sm">
              <button
                onClick={() => setSelectedQuality('standard')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedQuality === 'standard'
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Standard: 512x512, ~2s per sticker"
              >
                📷 Standard
                <div className="text-xs opacity-75 mt-0.5">512×512 · Fast</div>
              </button>
              <button
                onClick={() => setSelectedQuality('hd')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedQuality === 'hd'
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="HD: 1024x1024, ~15-20s per sticker"
              >
                ✨ HD
                <div className="text-xs opacity-75 mt-0.5">1024×1024 · Slower</div>
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-indigo-200">
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Sticker Shape</h3>
              <p className="text-sm text-gray-600">
                {selectedShape === 'shape'
                  ? 'Subject cut-out · transparent PNG · ~3s extra per sticker'
                  : 'Solid background · standard PNG'}
              </p>
            </div>
            <div className="flex gap-2 bg-white p-1.5 rounded-lg shadow-sm">
              <button
                onClick={() => setSelectedShape('square')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedShape === 'square'
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Square: solid background"
              >
                ▪ Square
                <div className="text-xs opacity-75 mt-0.5">Solid BG</div>
              </button>
              <button
                onClick={() => setSelectedShape('shape')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedShape === 'shape'
                    ? 'bg-pink-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Shape: transparent background cut-out"
              >
                ✦ Shape
                <div className="text-xs opacity-75 mt-0.5">Transparent</div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Generation Progress */}
      {generating && (
        <div className="card bg-indigo-50 border border-indigo-200 mb-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-medium text-indigo-900">
                {generationPhase === 'prompts' ? '🤖 Generating Prompts...' :
                 generationPhase === 'prompts-complete' ? '✅ Prompts Complete!' :
                 generationPhase === 'complete' ? '🎉 Generation Complete!' :
                 '🎨 Generating Stickers...'}
              </p>
              <span className="text-sm font-medium text-indigo-700">
                {generationProgress}%
              </span>
            </div>

            <div className="w-full bg-indigo-200 rounded-full h-3">
              <div
                className="bg-indigo-600 h-3 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                style={{ width: `${generationProgress}%` }}
              >
                {generationProgress > 10 && (
                  <span className="text-xs text-white font-medium">
                    {generationProgress}%
                  </span>
                )}
              </div>
            </div>

            {generationMessage && (
              <p className="text-sm text-indigo-700">
                {generationMessage}
              </p>
            )}

            <p className="text-xs text-indigo-600">
              💡 Images will appear below as they're generated. This takes 1-2 minutes.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Sticker Grid */}
        <div className="lg:col-span-2">
          <div className="card">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Sticker Grid
              {hasStickers && (
                <span className="text-lg font-normal text-gray-600 ml-2">
                  ({project.stickers.length}/12)
                </span>
              )}
            </h2>

            {!hasStickers ? (
              <div className="grid grid-cols-3 gap-4">
                {Array.from({ length: 12 }, (_, i) => (
                  <div
                    key={i}
                    className="aspect-square bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center"
                  >
                    <span className="text-gray-400 text-sm">#{i + 1}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {project.stickers.map((sticker) => (
                  <div
                    key={sticker.id}
                    className="aspect-square rounded-lg border border-gray-300 overflow-hidden group relative"
                    style={sticker.shape ? {
                      backgroundImage: 'linear-gradient(45deg, #d1d5db 25%, transparent 25%), linear-gradient(-45deg, #d1d5db 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d1d5db 75%), linear-gradient(-45deg, transparent 75%, #d1d5db 75%)',
                      backgroundSize: '16px 16px',
                      backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                      backgroundColor: 'white'
                    } : { backgroundColor: 'white' }}
                  >
                    <img
                      src={sticker.image_url}
                      alt={sticker.prompt}
                      className={`w-full h-full ${sticker.shape ? 'object-contain' : 'object-cover'}`}
                    />
                    {/* Loading overlay */}
                    {regeneratingStickerId === sticker.id && (
                      <div className="absolute inset-0 bg-black bg-opacity-75 flex flex-col items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2"></div>
                        <span className="text-white text-xs">Regenerating...</span>
                      </div>
                    )}
                    {/* Hover overlay with regenerate + shape toggle buttons */}
                    {regeneratingStickerId !== sticker.id && (
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex flex-col gap-2 items-center justify-center opacity-0 group-hover:opacity-100">
                        <button
                          onClick={() => handleRegenerateSticker(sticker.id, sticker.prompt, sticker.position)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium shadow-lg transition-all ${
                            selectedQuality === 'hd'
                              ? 'bg-purple-600 hover:bg-purple-700 text-white'
                              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                          }`}
                          title={`Regenerate with ${selectedQuality === 'hd' ? 'HD (1024x1024)' : 'Standard (512x512)'} quality`}
                        >
                          🔄 Regenerate {selectedQuality === 'hd' ? '(HD)' : ''}
                        </button>
                        <button
                          onClick={() => handleRegenerateSticker(sticker.id, sticker.prompt, sticker.position, !sticker.shape)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg transition-all ${
                            sticker.shape
                              ? 'bg-white text-gray-800 hover:bg-gray-100'
                              : 'bg-pink-500 hover:bg-pink-600 text-white'
                          }`}
                          title={sticker.shape ? 'Regenerate with solid background' : 'Regenerate with transparent background cut-out'}
                        >
                          {sticker.shape ? '▪ Make Square' : '✦ Make Shape'}
                        </button>
                        <div className="flex items-center gap-1 text-white text-xs bg-black bg-opacity-50 px-2 py-1 rounded">
                          <span>{sticker.quality === 'hd' ? '✨ HD' : '📷 Std'} · {sticker.shape ? '✦ Shape' : '▪ Square'}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {/* Fill remaining slots */}
                {project.stickers.length < 12 && Array.from(
                  { length: 12 - project.stickers.length },
                  (_, i) => (
                    <div
                      key={`empty-${i}`}
                      className="aspect-square bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-2 group relative"
                    >
                      <span className="text-gray-400 text-sm">
                        #{project.stickers.length + i + 1}
                      </span>
                      <button
                        onClick={handleRetryFailedSticker}
                        className="btn-secondary text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        🔄 Retry
                      </button>
                    </div>
                  )
                )}
              </div>
            )}

            {!hasStickers && (
              <div className="mt-6 text-center">
                <p className="text-gray-600 mb-4">
                  No stickers generated yet. Click "Generate Stickers" to create your sticker pack!
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Details */}
        <div className="lg:col-span-1 space-y-6">
          {/* Design Criteria */}
          <div className="card">
            <button
              onClick={() => setShowCriteria(!showCriteria)}
              className="w-full flex justify-between items-center mb-4"
            >
              <h3 className="text-lg font-bold text-gray-900">
                📋 Design Criteria
                <span className="text-sm font-normal text-gray-600 ml-2">
                  ({filledCriteriaCount}/10)
                </span>
              </h3>
              <span className="text-gray-500">
                {showCriteria ? '▼' : '▶'}
              </span>
            </button>

            {showCriteria && (
              <div className="space-y-3">
                {criteriaFields.map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {criteria[key] && criteria[key].trim() ? '✓ ' : ''}
                      {label}
                    </label>
                    <div className={`text-sm px-3 py-2 rounded border ${
                      criteria[key] && criteria[key].trim()
                        ? 'bg-green-50 border-green-200 text-gray-900'
                        : 'bg-gray-50 border-gray-200 text-gray-400'
                    }`}>
                      {criteria[key] && criteria[key].trim() ? criteria[key] : 'Not specified'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Conversation History */}
          {conversation.length > 0 && (
            <div className="card">
              <button
                onClick={() => setShowConversation(!showConversation)}
                className="w-full flex justify-between items-center mb-4"
              >
                <h3 className="text-lg font-bold text-gray-900">
                  💬 Conversation History
                  <span className="text-sm font-normal text-gray-600 ml-2">
                    ({conversation.length} messages)
                  </span>
                </h3>
                <span className="text-gray-500">
                  {showConversation ? '▼' : '▶'}
                </span>
              </button>

              {showConversation && (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {conversation.map((msg, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg ${
                        msg.role === 'user'
                          ? 'bg-indigo-50 border border-indigo-200'
                          : 'bg-gray-50 border border-gray-200'
                      }`}
                    >
                      <div className="text-xs font-medium text-gray-600 mb-1">
                        {msg.role === 'user' ? '👤 You' : '🤖 Assistant'}
                      </div>
                      <div className="text-sm text-gray-900 whitespace-pre-wrap">
                        {msg.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
